import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Menu, Popover } from "antd";
import { ConfigProvider } from "@ui-kit/ConfigProvider";
import { translate } from "@lib/translate";

import "./Select.css";
import { TabButton } from "@ui-kit/TabButton/TabButton";
import { IconButton } from "@ui-kit/IconButton/IconButton";

interface SelectProps {
  /**
   * Label to display on the button.
   */
  label?: string;
  /**
   * Items to display in the dropdown.
   */
  items: {
    label: string;
    value: string;
    iconPrefix?: React.ReactNode;
    style?: React.CSSProperties;
  }[];
  /**
   * Values that are currently selected.
   */
  selectedValues: string[];
  /**
   * Callback function to handle the selection of an item.
   */
  onChange: (values: string[]) => void;
  /**
   * Icon to display before the button label.
   */
  prefixIcon?: React.ReactNode;
  /**
   * Icon to display after the button label.
   */
  suffixIcon?: React.ReactNode;
  /**
   * Whether multiple items may be selected, will display footer with
   * clear and apply buttons as well.
   */
  isMultiSelect?: boolean;
}

const Select: FunctionComponent<SelectProps> = ({
  label,
  items,
  selectedValues = [],
  onChange,
  prefixIcon,
  suffixIcon,
  isMultiSelect = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<any>(null);
  const buttonRef = useRef<any>(null);

  const handleSelect = useCallback(
    (value: { selectedKeys: string[] }) => {
      if (!isMultiSelect) {
        // when only one option is possible, close when new selection made.
        setIsOpen(false);
      }
      onChange(value.selectedKeys);
    },
    [isMultiSelect, onChange],
  );

  const menuItems = useMemo(() => {
    return items.map((item) => ({
      label: item.label,
      key: item.value,
      icon: item.iconPrefix,
      style: item.style,
    }));
  }, [items]);

  const handlePress = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setIsOpen(!isOpen);
    },
    [isOpen],
  );

  const handleClear = useCallback(() => {
    onChange([]);
    setIsOpen(false);
  }, [onChange]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !selectRef.current?.contains(event.target as Node) &&
        !buttonRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <ConfigProvider type="client">
      <Popover
        styles={{
          body: {
            padding: 0,
          },
        }}
        open={isOpen}
        arrow={false}
        placement="bottom"
        content={
          <div className="select-component-inner" ref={selectRef}>
            <Menu
              items={menuItems}
              selectedKeys={selectedValues}
              multiple={isMultiSelect}
              onSelect={handleSelect}
              onDeselect={handleSelect}
              style={{
                borderRadius: "8px",
                maxHeight: "50vh",
                overflow: "auto",
              }}
            />
            {isMultiSelect && (
              <div className="select-actions">
                <Button type="text" onClick={handleClear}>
                  {translate("common.clear")}
                </Button>
                <Button
                  type="primary"
                  style={{ boxShadow: "none" }}
                  onClick={handlePress}>
                  {translate("common.apply")}
                </Button>
              </div>
            )}
          </div>
        }>
        {label ? (
          <TabButton
            variant="outline"
            ref={buttonRef}
            onPress={handlePress}
            iconPrefix={prefixIcon}
            label={label}
            iconSuffix={suffixIcon}
          />
        ) : (
          <IconButton icon={prefixIcon} onPress={handlePress} ref={buttonRef} />
        )}
      </Popover>
    </ConfigProvider>
  );
};

export { Select };
